import React, { useEffect, useRef, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import {
  HeaderFilterProvider,
  useHeaderFilters,
} from "../../context/HeaderFilterContext";
import { encryptQuery, decryptQuery } from "../../utils/encryption";
import useDynamicPageSize from "../../useDynamicPageSize";
import { toast, ToastContainer } from "react-toastify";
import Table from "../../core/common/dataTable";
import { DatePicker } from "antd";
import Footer from "../../Footer";
import dayjs, { Dayjs } from "dayjs";
import api from "../../api";
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";
import CommonSelect from "../../core/common/commonSelect";
import { useFilterData } from "../../context/FilterContext";
import { useIsMobile } from "../../hooks/useIsMobile";

interface HolidayType {
  id: number;
  holidayName: string;
  holidayFrom: string;
  holidayTo: string;
  holidayImage: string;
  value: string;
  label: string;
  isOptional?: number;
}

type ModalMode = "add" | "edit";

const Holidays: React.FC = () => {
  const isMobile = useIsMobile(991);
  // State variables
  const { filtersData } = useFilterData();
  const [totalItems, setTotalItems] = useState<number>(0);
  const [holidays, setHolidays] = useState<HolidayType[]>([]);
  const [modalMode, setModalMode] = useState<ModalMode>("add");
  const currentYear = dayjs().year();
  const [years, setYears] = useState<number[]>([]);
  const [selectedYear, setSelectedYear] = useState<string>(
    currentYear.toString()
  );
  // State for form errors
  const [formError, setFormError] = useState<{
    Holiday?: string;
    Date?: string;
  }>({});

  // State for image preview
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [userRole, setUserRole] = useState<number | null>(null);
  const [tableHeight, setTableHeight] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState<{
    Holiday: string;
    Date: Dayjs | null;
    ToDate: string;
    image?: File | string;
    isOptional: boolean;
  }>({
    Holiday: "",
    Date: null,
    ToDate: "",
    image: undefined,
    isOptional: false,
  });
  const [editId, setEditId] = useState<number | null>(null);

  // Refs for file input and submission state
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const isSubmittingRef = useRef<boolean>(false);

  // dynamic page size and header filters
  const pageSize = useDynamicPageSize();
  const { filters } = useHeaderFilters();
  const { setFilters } = useHeaderFilters();
  const [optionalHolidayTaken, setOptionalHolidayTaken] = useState<number>(0);
  const [optionalHolidays, setOptionalHolidays] = useState<any[]>([]);

  const navigate = useNavigate();
  const location = useLocation();
  const isInitialFetch = useRef(true);
  const { encryptedQuery } = useParams<{ encryptedQuery?: string }>(); // Get encryptedQuery from URL

  useEffect(() => {
    const calculateHeight = () => {
      const baseOffset = 194;
      const extraOffset = window.innerWidth <= 991 ? -43 : -81;
      const fixedOffset = baseOffset + extraOffset;

      const newHeight = window.innerHeight - fixedOffset;

      console.log("Calculated table height:", newHeight);
      console.log("fixedOffset:", fixedOffset);
      console.log("window.innerHeight:", window.innerHeight);

      setTableHeight(newHeight);
    };

    calculateHeight(); // initial call
    window.addEventListener("resize", calculateHeight); // respond to window size changes

    return () => window.removeEventListener("resize", calculateHeight); // cleanup on unmount
  }, []);
  // Handle Query Param Filters
  useEffect(() => {
    if (encryptedQuery) {
      try {
        const decoded = decodeURIComponent(encryptedQuery);
        const result = decryptQuery(decoded);

        if (result && typeof result === "object") {
          const { year, page } = result;
          if (year) setSelectedYear(String(year));
          if (page) setCurrentPage(Number(page));
          return;
        }
      } catch (e) {
        console.warn("Failed to decrypt holidays query:", e);
      }
    }

    // Default to current year if no query
    setSelectedYear(currentYear.toString());
    setCurrentPage(1);
  }, [encryptedQuery]);

  // Trigger fetch on dependency change
  useEffect(() => {
    if (isInitialFetch.current) {
      isInitialFetch.current = false;
      return;
    }
    fetchHolidays();
  }, [currentPage, pageSize, selectedYear]);

  // Build Filters UI
  useEffect(() => {
    const HolidaysFilters = () => (
      <div className="upper-absolute-class">
        <div className="Holiday-filters d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div className="d-flex holiday-full-width gap-2">
            {selectedYear !== currentYear.toString() && (
              <button
                className="btn add_btn_size btn-outline-primary reset-holidays"
                style={{ maxWidth: 200 }}
                onClick={() => {
                  setSelectedYear(currentYear.toString());
                  setCurrentPage(1);
                  navigate(`/holidays`, { replace: false });
                }}
              >
                <i className="ti ti-refresh" />
              </button>
            )}

            <div className="holiday-flex">
              <CommonSelect
                className="select"
                options={years.map((year) => ({
                  value: year.toString(),
                  label: year.toString(),
                }))}
                value={
                  selectedYear
                    ? {
                        value: selectedYear,
                        label: selectedYear,
                      }
                    : null
                }
                onChange={(selectedOption) => {
                  const year = selectedOption?.value || currentYear.toString();
                  setSelectedYear(year);
                  setCurrentPage(1);
                  updateFilterUrl({ year, page: 1 });
                }}
                forceFullWidth={isMobile}
              />
            </div>

            {userRole === 1 && (
              <button
                className="btn btn-outline-primary add_btn_size holiday-add-margin-right"
                onClick={() => openModal("add")}
              >
                <i className="ti ti-circle-plus" />
              </button>
            )}
          </div>
        </div>
      </div>
    );

    setFilters(<HolidaysFilters />);
    return () => {
      setFilters(null);
    };
  }, [selectedYear, userRole, years, isMobile, currentYear]);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const parsed = JSON.parse(userData);
        setUserRole(Number(parsed?.role_name));
        if (parsed?.optional_holiday_taken != null) {
       setOptionalHolidayTaken(Number(parsed.optional_holiday_taken));
     }
      } catch (e) {
        console.error("Failed to parse user data from localStorage", e);
      }
    }
  }, []);

  useEffect(() => {
    if (filtersData && Object.keys(filtersData).length > 0) {
      const responseYears = filtersData?.years || [];
      setYears(responseYears);

      // pick optional holidays array if provided by get-filters-data
   if (Array.isArray(filtersData.optional_holidays)) {
     setOptionalHolidays(filtersData.optional_holidays);
   }

      if (!selectedYear || !responseYears.includes(Number(selectedYear))) {
        if (responseYears.length > 0) {
          const firstYear = responseYears[0];
          setSelectedYear(firstYear.toString());
        }
      }
    }
  }, [filtersData]);

  // Fetch holidays from the API
  const fetchHolidays = async () => {
    try {
      setLoading(true);

      const yearToUse = selectedYear ? Number(selectedYear) : currentYear;

      const payload = {
        currentPage,
        pageSize,
        selectedYear: yearToUse,
      };

      const response = await api<any>("/holiday/filter-holidays", {
        method: "POST",
        body: payload,
      });

      if (response?.error) {
        console.error("API Error:", response.error);
        return;
      }

      const holidaysData = response.holidays?.data || [];
      const total = response.holidays?.total || 0;

      const mapped = holidaysData.map((item: any) => {
        const formattedFromDate = item.holiday_from || "";
        const formattedToDate = item.holiday_to || formattedFromDate;

        const readableDay = (() => {
          try {
            const parsedDate = new Date(
              formattedFromDate.split("-").reverse().join("-")
            );
            return parsedDate.toLocaleDateString("en-US", { weekday: "long" });
          } catch {
            return "Invalid Date";
          }
        })();

        return {
          id: item.id || `unknown-${Math.random()}`,
          holidayName: item.holidayName || "N/A",
          holidayFrom: formattedFromDate,
          holidayTo: formattedToDate,
          day: readableDay,
          holidayImage: item.holiday_image || null,
          isOptional: Number(item.is_optional ?? 0),
        };
      });

      setHolidays(mapped);
      setTotalItems(total);
    } catch (error: any) {
      console.error("Fetch Holidays Error:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (mode: ModalMode, record?: HolidayType) => {
    setModalMode(mode);
    setFormError({});
    setEditId(null);
    setImagePreview(null);

    if (fileInputRef.current) fileInputRef.current.value = "";

    if (mode === "edit" && record) {
      const parsedFromDate = dayjs(record.holidayFrom, "DD-MM-YYYY");
      const parsedToDate =
        record.holidayTo && record.holidayTo !== record.holidayFrom
          ? dayjs(record.holidayTo, "DD-MM-YYYY")
          : null;

      setFormData({
        Holiday: record.holidayName,
        Date: parsedFromDate,
        ToDate: parsedToDate ? parsedToDate.format("DD-MM-YYYY") : "",
        image: record.holidayImage || "", // Ensure the image URL is set in formData,
        isOptional: !!record.isOptional,
      });

      // Set the image preview to the existing image URL
      setImagePreview(
        typeof record.holidayImage === "string" ? record.holidayImage : null
      );
      setEditId(record.id);
    } else {
      setFormData({
        Holiday: "",
        Date: null,
        ToDate: "",
        image: undefined,
        isOptional: false,
      });
      setImagePreview(null); // Clear the image preview for new entries
    }

    const modalElement = document.getElementById("holiday_modal");
    if (modalElement) {
      modalElement.removeAttribute("aria-hidden");
      modalElement.removeAttribute("inert");
      const modalInstance = (window as any).bootstrap.Modal.getOrCreateInstance(
        modalElement
      );
      modalInstance.show();

      // Move focus to the modal
      const firstFocusableElement = modalElement.querySelector(
        "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])"
      );
      if (firstFocusableElement) {
        (firstFocusableElement as HTMLElement).focus();
      }
    }
  };

  const hideModal = (modalId: string) => {
    const el = document.getElementById(modalId);
    if (el && (window as any).bootstrap?.Modal) {
      const instance = (window as any).bootstrap.Modal.getInstance(el);
      if (instance) instance.hide();

      el.setAttribute("aria-hidden", "true");
      el.setAttribute("inert", "");

      setTimeout(() => {
        document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove());
        document.body.classList.remove("modal-open");
        document.body.style.overflow = "";

        const triggerElement = document.querySelector(
          `[data-bs-target="#${modalId}"]`
        );
        if (triggerElement) {
          (triggerElement as HTMLElement).focus();
        }
      }, 300);
    }
  };

  const openDeleteModal = (id: number) => {
    setEditId(id);
    const modalElement = document.getElementById("top-modal");
    if (modalElement) {
      const modalInstance = (window as any).bootstrap.Modal.getOrCreateInstance(
        modalElement
      );
      modalInstance.show();
    }
  };

  // delete holiday
  const handleDelete = async (id: number) => {
    try {
      const res = await api<any>(`/holiday/delete/${id}`, {
        method: "DELETE",
      });

      if (res.success) {
        setHolidays((prev) => prev.filter((h) => h.id !== id));
        setTotalItems((prevCount) => prevCount - 1);
        toast.success("Holiday deleted!");
        // Calculate the total pages after deletion
        const totalPages = Math.ceil((totalItems - 1) / pageSize);
        // If the current page number is now greater than totalPages, move back one page
        if (currentPage > totalPages) {
          setCurrentPage(totalPages > 0 ? totalPages : 1);
          updateFilterUrl({ year:selectedYear, page: totalPages > 0 ? totalPages : 1 });
        } else {
          fetchHolidays();
        }
      } else {
        toast.error(res.error || "Failed to delete.");
      }
    } catch (err: any) {
      toast.error(err.message || "Something went wrong.");
    }
  };

  // Get the modal container for DatePicker
  const getModalContainer = () =>
    document.getElementById("modal-datepicker") || document.body;

  // Handle page change for pagination
  const handlePageChange = (page: number) => {
    console.log("Changing to page:", page); // Debugging log
    setCurrentPage(page); // Update currentPage state
    updateFilterUrl({ year: selectedYear || "2025", page }); // Update URL with encrypted query
  };

  const defaultHolidayFilters = {
    year: currentYear.toString(),
    page: 1,
  };

  const areHolidayFiltersDefault = (filters: {
    year: string;
    page: number;
  }) => {
    return (
      filters.year === defaultHolidayFilters.year &&
      filters.page === defaultHolidayFilters.page
    );
  };

  const updateFilterUrl = (filters: { year: string; page: number }) => {
    if (areHolidayFiltersDefault(filters)) {
      navigate("/holidays", { replace: false });
    } else {
      const encrypted = encryptQuery(filters);
      const encoded = encodeURIComponent(encrypted);
      navigate(`/holidays/${encoded}`, { replace: false });
    }
  };

  // Handle input change for form fields
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, files } = e.target;

    if (name === "image" && files && files[0]) {
      const file = files[0];
      const reader = new FileReader();

      reader.onload = () => {
        setImagePreview(reader.result as string);
        setFormData((prev) => ({ ...prev, image: file }));
      };

      reader.readAsDataURL(file); // Read the file as a data URL
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (isSubmittingRef.current) return;

    const errors: { Holiday?: string; Date?: string } = {}; // Define errors object

    // Validate Holiday Title
    if (!formData.Holiday.trim()) {
      errors.Holiday = "Holiday title is required.";
    }

    // Validate From Date
    if (!formData.Date) {
      errors.Date = "From Date is required.";
    }

    if (Object.keys(errors).length > 0) {
      setFormError(errors); // Set errors in state
      return;
    }

    setFormError({}); // Clear errors if validation passes

    isSubmittingRef.current = true;
    const body = new FormData();
    body.append("holidayName", formData.Holiday.trim());
    body.append(
      "holidayFrom",
      formData.Date ? formData.Date.format("YYYY-MM-DD") : ""
    );
    body.append(
      "holidayTo",
      formData.ToDate
        ? dayjs(formData.ToDate, "DD-MM-YYYY").format("YYYY-MM-DD")
        : ""
    );
    body.append("is_optional", formData.isOptional ? "1" : "0");

    // Append the image as binary if it exists
    if (formData.image instanceof File) {
      console.log("Appending image as File:", formData.image);
      body.append("holiday_image", formData.image); // Append binary file
    } else if (modalMode === "edit" && typeof formData.image === "string") {
      console.log("Appending existing image URL:", formData.image);
      body.append("holiday_image", formData.image); // Append existing image URL
    } else {
      console.log("No image provided, skipping holiday_image.");
    }

    if (modalMode === "edit" && editId !== null) {
      body.append("id", String(editId));
    }

    // Debugging log for FormData content
    console.log("FormData content before API call:");
    for (const [key, value] of body.entries()) {
      if (key === "holiday_image" && value instanceof File) {
        console.log(
          `${key}: File { name: ${value.name}, size: ${value.size} }`
        );
      } else {
        console.log(`${key}:`, value);
      }
    }

    try {
      const res = await api<any>("/holiday/save", {
        method: "POST",
        headers: {
          "Content-Type": "multipart/form-data", // Explicitly set content type
        },
        body,
      });

      console.log("API Response:", res);

      if (res.success) {
        await fetchHolidays();
        hideModal("holiday_modal");
        toast.success(
          modalMode === "add" ? "Holiday added!" : "Holiday updated!"
        );
      } else {
        toast.error(res.error || "Failed to save holiday.");
      }
    } catch (err: any) {
      console.error("API Error:", err.message);
      toast.error("Holiday is already added.");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
      isSubmittingRef.current = false;
    }
  };

  // Define columns for the table
  const columns = [
    {
      title: "Holiday Name",
      dataIndex: "holidayName",
      render: (_: any, rec: HolidayType) => (
        <div className="d-flex align-items-center">
          <div
            style={{
              width: 32,
              height: 32,
              objectFit: "cover",
              borderRadius: "50%",
              overflow: "hidden",
              // backgroundColor: "#f0f0f0",
            }}
          >
            {rec.holidayImage && (
              <img
                src={rec.holidayImage}
                alt={rec.holidayName}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            )}
          </div>
          <span className="ms-2 fw-medium fs-14 text-dark">
            {rec.holidayName}
          </span>
        </div>
      ),
    },

    {
      title: "Dates",
      dataIndex: "holidayFrom",
      render: (_: any, record: HolidayType) => {
        const fromDate = dayjs(record.holidayFrom, "DD-MM-YYYY").format(
          "DD-MMM-YYYY"
        );
        const toDate =
          record.holidayTo && record.holidayTo !== record.holidayFrom
            ? dayjs(record.holidayTo, "DD-MM-YYYY").format("DD-MMM-YYYY")
            : null;

        return (
          <span>
            {fromDate}
            {toDate ? ` to ${toDate}` : ""}
          </span>
        );
      },
    },
    {
      title: "Day",
      dataIndex: "day",
      render: (_: any, record: HolidayType) => {
        const fromDay = dayjs(record.holidayFrom, "DD-MM-YYYY").format("dddd");
        const toDay =
          record.holidayTo && record.holidayTo !== record.holidayFrom
            ? dayjs(record.holidayTo, "DD-MM-YYYY").format("dddd")
            : null;

        return (
          <span>
            {fromDay}
            {toDay ? ` - ${toDay}` : ""}
          </span>
        );
      },
    },
    ...(userRole === 1
      ? [
          {
            title: "Actions",
            dataIndex: "actions",
            render: (_: any, record: HolidayType) => (
              <div className="action-icon d-inline-flex gap-2">
                <Link to="#" onClick={() => openModal("edit", record)}>
                  <i className="ti ti-edit" />
                </Link>
                <Link to="#" onClick={() => openDeleteModal(record.id)}>
                  <i className="ti ti-trash" />
                </Link>
              </div>
            ),
          },
        ]
      : []),
  ].filter(Boolean);

  // render the component
  return (
    <>
      <HeaderFilterProvider>
        <div className="page-wrapper d-flex flex-column">
          
          <div className="content flex-grow-1">
            {/* <div className="d-flex justify-content-between align-items-center mb-3">
            <button
              className="btn btn-primary"
              onClick={() => openModal("add")}
            >
              Add Holiday
            </button>
          </div> */}

            <div className="card card-margin-bottom">
              <div
                className="card-body p-0"
                id="Holiday-list"
                style={{
                  height: tableHeight,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Table
                  style={{ flex: 1 }}
                  dataSource={holidays} // Ensure this is correctly passed
                  columns={columns}
                  onPageChange={handlePageChange}
                  totalItems={totalItems}
                  pageSize={pageSize}
                  currentPage={currentPage}
                  loading={loading}
                />
              </div>
            </div>
          </div>

          {/* Add/Edit Modal */}
          <div className="modal fade" id="holiday_modal">
            <div className="modal-dialog modal-md modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h4 className="modal-title">
                    {modalMode === "add" ? "Add Holiday" : "Edit Holiday"}
                  </h4>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => hideModal("holiday_modal")}
                  />
                </div>

                {/* Holiday title*/}
                <div className="modal-body pb-0">
                  <div className="mb-3">
                    <label className="form-label">
                      Holiday Title <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="Holiday"
                      value={formData.Holiday}
                      onChange={(e) => {
                        setFormData((prev) => ({
                          ...prev,
                          Holiday: e.target.value,
                        }));
                        setFormError((prev) => ({ ...prev, Holiday: "" })); // Clear specific error on input change
                      }}
                      placeholder="Enter holiday name"
                    />
                    {formError.Holiday && (
                      <div className="text-danger">{formError.Holiday}</div> // Display validation message
                    )}
                  </div>

                  {/* Date pickers for From and To dates */}
                  <div className="mb-3 position-relative">
                    <label className="form-label">
                      From Date <span className="text-danger">*</span>
                    </label>
                    <div className="input-icon-end position-relative">
                      <DatePicker
                        className="form-control datetimepicker ps-15"
                        value={formData.Date} // Ensure this is a Dayjs object
                        onChange={(date) => {
                          setFormData((prev) => ({ ...prev, Date: date })); // Update From Date as Dayjs
                          setFormError((prev) => ({ ...prev, Date: "" })); // Clear specific error on input change
                        }}
                        format="DD-MMM-YYYY"
                        getPopupContainer={getModalContainer}
                        placeholder="DD-MMM-YYYY"
                        disabledDate={(current) =>
                          formData.ToDate
                            ? current &&
                              current.isAfter(
                                dayjs(formData.ToDate, "DD-MM-YYYY").subtract(
                                  1,
                                  "days"
                                )
                              )
                            : false
                        }
                      />
                      <span className="input-icon-addon">
                        <i className="ti ti-calendar text-gray-7" />
                      </span>
                    </div>
                    {formError.Date && (
                      <div className="text-danger">{formError.Date}</div> // Display validation message
                    )}
                  </div>

                  <div className="mb-3 position-relative">
                    <label className="form-label">To Date</label>
                    <div className="input-icon-end position-relative">
                      <DatePicker
                        className="form-control datetimepicker ps-15"
                        value={
                          formData.ToDate
                            ? dayjs(formData.ToDate, "DD-MM-YYYY")
                            : null
                        } // Convert ToDate to Dayjs
                        onChange={(date) =>
                          setFormData((prev) => ({
                            ...prev,
                            ToDate: date ? date.format("DD-MM-YYYY") : "",
                          }))
                        }
                        format="DD-MMM-YYYY"
                        getPopupContainer={getModalContainer}
                        placeholder="DD-MMM-YYYY"
                        allowClear 
                        inputReadOnly={false} // allow typing / backspace
                        onKeyDown={(e) => {
                          if (e.key === "Backspace" || e.key === "Delete") {
                            // let user clear via keyboard
                            setFormData((prev) => ({ ...prev, ToDate: "" }));
                          }
                        }}
                      onBlur={(e) => {
                        const input = e.target as HTMLInputElement;
                        if (!input.value) {
                          setFormData((prev) => ({ ...prev, ToDate: "" }));
                        }
                      }}

                        disabledDate={(current) =>
                          formData.Date
                            ? current &&
                              current.isBefore(
                                dayjs(formData.Date, "DD-MM-YYYY").add(
                                  1,
                                  "days"
                                )
                              )
                            : false
                        } // Ensure To Date cannot be before From Date
                      />
                      <span className="input-icon-addon"  style={{ pointerEvents: "none" }}>
                        <i className="ti ti-calendar text-gray-7" />
                      </span>
                    </div>
                  </div>

                  <div className="mb-2">
                    <div className="form-check">
                      <input
                        id="holiday-optional"
                        className="form-check-input"
                        type="checkbox"
                        checked={formData.isOptional}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, isOptional: e.target.checked }))
                        }
                      />
                      <label className="form-check-label" htmlFor="holiday-optional">
                        Optional holiday
                      </label>

                      {/* inside modal header or body if you like
                      {optionalHolidayTaken != null && (
                        <small className="text-muted">
                          Optional holidays taken: {optionalHolidayTaken}
                          {Array.isArray(optionalHolidays) ? ` · available: ${optionalHolidays.length}` : ""}
                        </small>
                      )} */}
                    </div>
                  </div>


                  {/* File input for image upload */}
                  <div className="mb-3">
                    <label className="form-label">Image </label>
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="form-control"
                      accept="image/*"
                      name="image"
                      onChange={handleInputChange}
                    />
                    {imagePreview && (
                      <div className="mt-2">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          style={{
                            maxWidth: "100px",
                            height: "100px",
                            objectFit: "cover",
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-light me-2"
                    onClick={() => hideModal("holiday_modal")}
                  >
                    Cancel
                  </button>
                  <button className="btn btn-primary" onClick={handleSubmit}>
                    {modalMode === "add" ? "Add" : "Update"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Delete confirmation modal */}
          <div id="top-modal" className="modal fade" tabIndex={-1}>
            <div className="modal-dialog modal-top">
              <div className="modal-content">
                <div className="modal-header">
                  <h4 className="modal-title">Delete</h4>
                  <button
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body">
                  <p>Are you sure you want to delete this record?</p>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-cancel" data-bs-dismiss="modal">
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => editId !== null && handleDelete(editId)}
                    data-bs-dismiss="modal"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>

          <Footer />
        </div>
      </HeaderFilterProvider>
      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
};

export default Holidays;